/** Preview đề xuất Tra cứu AI — nhóm theo bước, render HTML an toàn. */
import React from 'react';
import { Button, Checkbox, Input, Tag, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { AiProposal } from '@/types/ai-agent';
import type { DiffRow } from '../adapters/types';
import type { BscResultView } from './bscStepAiConfig';
import styles from '../AiStepAssistant.module.css';
import { toSafeHtml } from '@/utils/sanitizeHtml'
const OP_TAG: Record<string, { color: string; label: string }> = {
  add: { color: 'green', label: 'Thêm' },
  update: { color: 'gold', label: 'Sửa' },
  delete: { color: 'red', label: 'Xóa' },
};

const SWOT_ORDER = ['Điểm mạnh', 'Điểm yếu', 'Cơ hội', 'Thách thức'];
const STRATEGY_ORDER = ['Chiến lược SO', 'Chiến lược WO', 'Chiến lược ST', 'Chiến lược WT'];

type Props = {
  resultView: BscResultView;
  proposal: AiProposal;
  rows: DiffRow[];
  selected: Set<string>;
  toggle: (ref: string, on?: boolean) => void;
  edits: Record<string, string>;
  edit: (ref: string, text: string) => void;
  editing: string | null;
  setEditing: (ref: string | null) => void;
  hideSelection?: boolean;
};

const proposalByRef = (proposal: AiProposal) =>
  new Map(proposal.items.map((p) => [p.target.ref, p]));

export const BscProposeRichPreview: React.FC<Props> = ({
  resultView,
  proposal,
  rows,
  selected,
  toggle,
  edits,
  edit,
  editing,
  setEditing,
  hideSelection = false,
}) => {
  const byRef = proposalByRef(proposal);

  const renderRowCard = (row: DiffRow) => {
    const on = selected.has(row.ref);
    const htmlRaw = row.htmlAfter;
    const html = edits[row.ref] ?? htmlRaw;
    const after = edits[row.ref] ?? row.after;
    const pItem = byRef.get(row.ref);
    const left = (pItem?.links?.left_ids as string[] | undefined) ?? [];
    const right = (pItem?.links?.right_ids as string[] | undefined) ?? [];
    return (
      <div
        key={row.ref}
        className={`${styles.row} ${hideSelection ? styles.rowNoSelect : ''} ${!hideSelection && on ? styles.rowSelected : ''}`}
      >
        {!hideSelection ? (
          <Checkbox checked={on} onChange={(e) => toggle(row.ref, e.target.checked)} aria-label={`Chọn ${row.ref}`} />
        ) : null}
        <div className={styles.rowBody}>
          <div className={styles.rowHead}>
            <Tag color={OP_TAG[row.op]?.color}>{OP_TAG[row.op]?.label}</Tag>
            <Tag>{row.sectionLabel}</Tag>
            {row.editable && editing !== row.ref && row.op !== 'delete' ? (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => setEditing(row.ref)}
              >
                Sửa
              </Button>
            ) : null}
          </div>
          {row.before && row.op !== 'add' ? <div className={styles.before}>{row.before}</div> : null}
          {row.op === 'delete' ? null : editing === row.ref ? (
            <Input.TextArea
              autoFocus
              value={htmlRaw && (resultView === 'html_strategy' || resultView === 'html_result') ? html : after}
              autoSize={{ minRows: 2, maxRows: 12 }}
              onChange={(e) => edit(row.ref, e.target.value)}
              onBlur={() => setEditing(null)}
            />
          ) : htmlRaw && (resultView === 'html_strategy' || resultView === 'html_result') ? (
            <div className={styles.htmlPreview} dangerouslySetInnerHTML={toSafeHtml(html)} />
          ) : (
            <div className={styles.after}>{after}</div>
          )}
          {(left.length > 0 || right.length > 0) && (
            <div className={styles.linkChips}>
              {left.map((id) => (
                <Tag key={`l-${id}`} className={styles.linkChip}>
                  SWOT {id.slice(0, 8)}
                </Tag>
              ))}
              {right.map((id) => (
                <Tag key={`r-${id}`} className={styles.linkChip}>
                  O/T {id.slice(0, 8)}
                </Tag>
              ))}
            </div>
          )}
          {row.rationale ? <Typography.Text type="secondary" className={styles.rationale}>{row.rationale}</Typography.Text> : null}
          {row.warning ? <Typography.Text type="warning">{row.warning}</Typography.Text> : null}
        </div>
      </div>
    );
  };

  const groupRows = (order: string[]) => {
    const buckets = new Map<string, DiffRow[]>();
    order.forEach((k) => buckets.set(k, []));
    const other: DiffRow[] = [];
    rows.forEach((row) => {
      const key = order.find((o) => row.sectionLabel.includes(o) || row.sectionLabel === o);
      if (key) buckets.get(key)!.push(row);
      else other.push(row);
    });
    return { buckets, other };
  };

  if (resultView === 'html_strategy') {
    const { buckets, other } = groupRows(STRATEGY_ORDER);
    return (
      <div className={styles.previewGroups}>
        {STRATEGY_ORDER.map((label) => {
          const list = buckets.get(label) ?? [];
          if (!list.length) return null;
          return (
            <section key={label} className={styles.previewGroup}>
              <Typography.Title level={5} className={styles.previewGroupTitle}>
                {label}
              </Typography.Title>
              {list.map(renderRowCard)}
            </section>
          );
        })}
        {other.map(renderRowCard)}
      </div>
    );
  }

  if (resultView === 'html_result') {
    return <div className={styles.previewGroups}>{rows.map(renderRowCard)}</div>;
  }

  if (resultView === 'list' && rows.some((r) => SWOT_ORDER.some((s) => r.sectionLabel.includes(s)))) {
    const { buckets, other } = groupRows(SWOT_ORDER);
    return (
      <div className={styles.previewGroups}>
        {SWOT_ORDER.map((label) => {
          const list = buckets.get(label) ?? [];
          if (!list.length) return null;
          return (
            <section key={label} className={styles.previewGroup}>
              <Typography.Title level={5} className={styles.previewGroupTitle}>
                {label}
              </Typography.Title>
              {list.map(renderRowCard)}
            </section>
          );
        })}
        {other.map(renderRowCard)}
      </div>
    );
  }

  return <>{rows.map(renderRowCard)}</>;
};

export default BscProposeRichPreview;
